import {
  IDepartmentModel,
  IEmailModel,
  IGroupModel,
  IPhoneModel,
  IProviderModel,
} from 'store/models/phoneDirectoriesStore';
import {
  checkValidEmail,
  findFiasTreeHouseById,
  getPartialUrlValidationErrMsg,
  getPlainText,
} from 'utils';

export const getCurrentDictionaryHouseBindingTypeMsg = (
  bindedHousesLength: number,
  bindedHouseGroupsLength: number
) => {
  return bindedHousesLength && bindedHouseGroupsLength
    ? 'Дома/Группы'
    : bindedHouseGroupsLength
      ? 'Группы'
      : 'Дома';
};

export const filterGroupsByHouseBindingsCallback = (
  housesGroups: { [propName: string]: any },
  group: IGroupModel,
  requestHouseId: string
) => {
  return (
    housesGroups
      .filter((houseGroupObj: { [propName: string]: any }) =>
        group.house_binding!.house_groups.includes(houseGroupObj.id)
      )
      .find((houseGroupObj: { [propName: string]: any }) =>
        houseGroupObj.houses.includes(requestHouseId)
      ) ||
    group.house_binding!.houses.find(
      (houseId: string) => houseId === requestHouseId
    )
  );
};

export const filterRosclastersProviders = (
  providerList: IProviderModel[],
  usersProviderId: string
) => {
  return providerList.filter((org) => org.provider !== usersProviderId);
};

export const checkInRange = (name: string, from: number, to: number) => {
  return name.replace(/\s+/g, '').length >= from && name.length <= to;
};

export const isProviderDescriptionLengthValid = (html: string): boolean => {
  if (html.length > 20000) {
    return false;
  }

  const visibleLength = getPlainText(html).trim().length;

  return visibleLength >= 3 && visibleLength <= 5000;
};

export const withInvalidContacts = (group: IGroupModel) => {
  return (
    group.departments.every(
      (department: IDepartmentModel) =>
        !department.name &&
        !department.phones.length &&
        !department.emails.length
    ) ||
    group.departments.some((department: IDepartmentModel) => {
      return (
        ((department.name ||
          department.phones.length ||
          department.emails.length) &&
          (!checkInRange(department.name, 3, 100) ||
            (!department.phones.length && !department.emails.length) ||
            !department.phones.every((phoneObj: IPhoneModel) => {
              return (
                (!phoneObj.phone_name ||
                  checkInRange(phoneObj.phone_name, 3, 100)) &&
                phoneObj.code.length === 3 &&
                phoneObj.number.length === 7 &&
                (!phoneObj.add || checkInRange(phoneObj.add, 3, 12))
              );
            }))) ||
        !department.emails.every((emailObj: IEmailModel) => {
          return (
            checkValidEmail(emailObj.email_address) &&
            (!emailObj.email_name || checkInRange(emailObj.email_name, 3, 100))
          );
        })
      );
    })
  );
};

export const lockValidation = (
  provider: IProviderModel,
  groups: IGroupModel[],
  invalidBeingAddedContacts: {
    email: boolean;
    phone: boolean;
  },
  housesBinded: boolean
): boolean => {
  const isProviderInvalid =
    !checkInRange(provider.organization, 3, 100) ||
    !isProviderDescriptionLengthValid(provider.description) ||
    (!!provider.website?.length &&
      !!getPartialUrlValidationErrMsg(provider.website));

  const areAnyGroupsInvalid = groups.some(
    (group) =>
      !checkInRange(group.group_name, 3, 100) || withInvalidContacts(group)
  );

  const areContactsInvalid = Object.values(invalidBeingAddedContacts).includes(
    true
  );

  const isHouseBindingInvalid = !housesBinded;

  return (
    isProviderInvalid ||
    areAnyGroupsInvalid ||
    areContactsInvalid ||
    isHouseBindingInvalid
  );
};

export const lockProviderValidation = (provider: IProviderModel): boolean => {
  const isOrganizationInvalid = !checkInRange(provider.organization, 3, 100);
  const isDescriptionLengthInvalid = !isProviderDescriptionLengthValid(
    provider.description
  );
  const isWebsiteInvalid =
    !!provider.website?.length &&
    !!getPartialUrlValidationErrMsg(provider.website);

  return (
    isOrganizationInvalid || isDescriptionLengthInvalid || isWebsiteInvalid
  );
};

export const lockGroupValidation = (
  group: IGroupModel,
  housesBinded: boolean
): boolean => {
  const isGroupNameInvalid = !checkInRange(group.group_name, 3, 100);
  const hasInvalidContacts = withInvalidContacts(group);
  const isHouseBindingInvalid = !housesBinded;

  return isGroupNameInvalid || hasInvalidContacts || isHouseBindingInvalid;
};

export const prepareHousesBindsParams = (
  fias: { [propName: string]: any },
  unavailableToSetGroupHouseBindings?: string[],
  unavailableToSetGroupHouseGroupsBinding?: string[]
) => {
  const houseBindings = ['house_groups', 'houses'].reduce(
    (acc: any, fieldName: string) => ({
      ...acc,
      [fieldName]: fias[
        fieldName === 'house_groups'
          ? 'selectedGroups'
          : 'confirmedSelectedHouses'
      ]
        .filter(
          (item: any) => fieldName === 'house_groups' || !item.houses // фильтруем группы
        )
        .map(
          (houseBindFieldObj: { [propName: string]: any }) =>
            houseBindFieldObj.id || houseBindFieldObj._id
        ),
    }),
    {}
  );

  if (unavailableToSetGroupHouseBindings) {
    houseBindings.houses.unshift(...unavailableToSetGroupHouseBindings);
  }

  if (unavailableToSetGroupHouseGroupsBinding) {
    houseBindings.house_groups.unshift(
      ...unavailableToSetGroupHouseGroupsBinding
    );
  }

  houseBindings.house_groups = Array.from(new Set(houseBindings.house_groups));
  houseBindings.houses = Array.from(new Set(houseBindings.houses));

  return houseBindings;
};

export const checkHouseBindingDifferences = (
  selectedHouses: any[],
  unavailableToSetGroupHouseBinding: string[],
  currentGroup: IGroupModel
) => {
  return (
    selectedHouses.length !==
      currentGroup.house_binding!.houses.length -
        unavailableToSetGroupHouseBinding.length ||
    currentGroup.house_binding!.houses.some(
      (houseId) =>
        (typeof selectedHouses[0] === 'string'
          ? !selectedHouses.some((id: string) => id === houseId)
          : !selectedHouses.some(
              (houseObj: { [propName: string]: any }) =>
                houseObj._id === houseId
            )) &&
        !unavailableToSetGroupHouseBinding.some((id) => id === houseId)
    )
  );
};

// TODO возможно стоит забирать дома из групп и проверять их вместе с выбранными домами
export const checkHouseGroupsBindingDifferences = (
  selectedHouseGroups: { [propName: string]: any }[],
  unavailableToSetGroupHouseGroupsBinding: string[],
  currentGroup: IGroupModel
): boolean => {
  return (
    selectedHouseGroups.length !==
      (currentGroup.house_binding?.house_groups.length || 0) -
        unavailableToSetGroupHouseGroupsBinding.length ||
    !!currentGroup.house_binding?.house_groups.some(
      (houseGroupId) =>
        !selectedHouseGroups.some(
          (houseGroupObj) => houseGroupObj.id === houseGroupId
        ) &&
        !unavailableToSetGroupHouseGroupsBinding.some(
          (id) => id === houseGroupId
        )
    )
  );
};

export const buildFiasSearchHouseBinding = (
  currentGroup: IGroupModel,
  houseGroups: { [propName: string]: any }[],
  tree: { [propName: string]: any }[],
  setCurrentSelectedHousesCallback: Function,
  setConfirmedSelectedHousesCallback: Function
) => {
  let groups: { [propName: string]: any }[] = [];
  const houses: { _id: string; short_address: string; parent?: string }[] = [];

  if (currentGroup.house_binding?.house_groups.length) {
    groups = houseGroups.filter((item: any) =>
      currentGroup.house_binding!.house_groups.includes(item.id)
    );
  }

  if (currentGroup.house_binding?.houses.length) {
    currentGroup.house_binding!.houses.forEach((houseId) => {
      findFiasTreeHouseById(tree, houseId, houses, true);
    });
  }

  setCurrentSelectedHousesCallback([...groups, ...houses]);
  setConfirmedSelectedHousesCallback([...groups, ...houses]);
};
